import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { TertiaryButton } from '@/components/button/Button.jsx';
import { CommandPaletteContext } from '../../shell/command-palette/CommandPaletteProvider.jsx';
import { resolvePageMenu } from './navLookup.js';
import { useMenuNavTree } from './useMenuNavTree.js';
import { PageMenu } from './PageMenu.jsx';
import { PageHeaderTabs } from './PageHeaderTabs.jsx';
import { useSelector } from 'react-redux';
import { faEarthAmericas, faEye, faGamepad } from '@fortawesome/free-solid-svg-icons';
import InfoTooltip from '../tooltip/InfoTooltip.jsx';
import { gamePageScope } from '../../utils/pageScope.js';
import './PageHeader.css';

/**
 * On a game page, what an edit here affects: "Whole game" on a game-wide page, the geography (or "All players") on a
 * geography page - and "View only" for a geography-only member on a game-wide page, whose changes would reach every
 * player. Decided 9 October 2026; the server enforces the same rule.
 */
const ScopePill = ({ pathname }) => {
    const scope = gamePageScope(pathname);
    const segmentName = useSelector(state => state.dashboardContext?.selectedSegmentName);
    const wholeGameAccess = useSelector(state => state.dashboardContext?.wholeGameAccess);
    if (!scope) return null;
    const gameWide = scope === 'game';
    const label = gameWide ? 'Whole game' : (segmentName || 'All players');
    const tip = gameWide
        ? 'This page applies to the whole game - every player, whichever geography you are viewing.'
        : (segmentName ? `You are working in ${segmentName}.` : 'You are working with all players of the game.');
    return (
        <>
            <InfoTooltip text={tip}>
                <span className={`page-header-scope-pill page-header-scope-pill--${scope}`}>
                    <FontAwesomeIcon icon={gameWide ? faGamepad : faEarthAmericas} />
                    {label}
                </span>
            </InfoTooltip>
            {gameWide && wholeGameAccess === false && (
                <InfoTooltip text="Your access is to geographies of this game, so you can view this page but not change it: changes here apply to every player. Ask a game owner.">
                    <span className="page-header-scope-pill page-header-scope-pill--view-only">
                        <FontAwesomeIcon icon={faEye} />
                        View only
                    </span>
                </InfoTooltip>
            )}
        </>
    );
};

export const PageHeader = ({
    title,
    pagePath,
    actions,
    onNavigate,
    /* Leaving affordance for a page you entered FROM somewhere — a form, a detail view. The page
       owns what it means, because a form usually has to ask about unsaved work before it goes. */
    onBack,
    backLabel = 'Back',
    // Optional render slot displayed immediately BEFORE the title button, inside
    // `page-header-left`. For a page reached through a path, where the title is the
    // last step of it: the earlier steps go here, so the trail reads left to right
    // and the dropdown still hangs off the page you are actually on.
    titleLead,
    // Optional render slot displayed immediately beside the title button
    // (inside `page-header-left`) but OUTSIDE the title's own affordance.
    // Use this for badges / pills that describe the page state — e.g. the
    // Notifications page's "12 unread" pill — so they don't get visually
    // absorbed into the title's clickable region.
    titleAdornment,
    // Optional tab strip. When present, the header grows a second row below
    // the title: tabs on the left, `actions` on the right. `tabs` is
    // [{ key, label }]; the page owns selection via `activeTab` / `onTabChange`
    // (so this is a pure presentational swap for the old in-actions radio strip).
    tabs,
    activeTab,
    onTabChange,
    // The "Whole game" / geography / "View only" pills say what an edit on THIS page affects. Off for a screen
    // that shows no page - a game the user cannot open - where they described the selected game instead and
    // read as "View only" access to a game the user is suspended on.
    showScope = true,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const palette = useContext(CommandPaletteContext);
    const openCommandPalette = palette?.open || (() => {});

    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const navTree = useMenuNavTree();
    const context = useMemo(
        () => resolvePageMenu(pagePath, location.pathname, navTree),
        [pagePath, location.pathname, navTree]
    );

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            // Flyout sub-menus are portaled to document.body, so a DOM
            // contains() check on rootRef misses them. Treat any click
            // inside a flyout wrap as inside the menu.
            if (rootRef.current?.contains(e.target)) return;
            if (e.target.closest?.('.page-menu-flyout-wrap')) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const handlePick = (path) => {
        if (onNavigate) onNavigate(path);
        else navigate(path);
    };

    const hasTabs = tabs?.length > 0;

    return (
        <div className="content-header page-header" ref={rootRef}>
            <div className="page-header-main">
                <div className="page-header-left">
                    {onBack && (
                        <TertiaryButton
                            className="page-header-back"
                            size="small"
                            icon={faAngleLeft}
                            onClick={onBack}
                            title={backLabel}
                            aria-label={backLabel}
                        />
                    )}
                    {titleLead && (
                        <div className="page-header-lead">{titleLead}</div>
                    )}
                    <button
                        type="button"
                        className={`page-header-trigger ${open ? 'open' : ''}`}
                        onClick={() => setOpen(v => !v)}
                        aria-haspopup="menu"
                        aria-expanded={open}
                    >
                        <h1 className="page-header-title" title={typeof title === 'string' ? title : undefined}>{title}</h1>
                        <FontAwesomeIcon
                            icon={open ? faAngleUp : faAngleDown}
                            className="page-header-caret"
                        />
                    </button>
                    {open && (
                        <PageMenu
                            context={context}
                            currentPath={context?.currentNode?.path}
                            onPick={handlePick}
                            onSearch={openCommandPalette}
                            onClose={() => setOpen(false)}
                        />
                    )}
                    {showScope && <ScopePill pathname={location.pathname} />}
                    {titleAdornment && (
                        <div className="page-header-title-adornment">{titleAdornment}</div>
                    )}
                </div>
                {/* With tabs the actions move down beside the tab strip. */}
                {actions && !hasTabs && <div className="page-header-actions">{actions}</div>}
            </div>
            {hasTabs && (
                <div className="page-header-subheader">
                    <PageHeaderTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
                    {actions && <div className="page-header-actions">{actions}</div>}
                </div>
            )}
        </div>
    );
};

export default PageHeader;
